import { Currency, type Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	derogatoryCount,
	type ExperianCreditDetail,
	formatMonthYear,
	formatReportDate,
	formatReportDollars,
} from "@portal/api/account/admin/individualCreditReport";
import { MetricCard } from "@portal/components/patterns";
import { Card, Tag, Text } from "@portal/components/primitives";
import { DateTime } from "luxon";
import type { FC } from "react";
import { Fact, ReportSection, StatusSummary } from "./ReportSection";

type Detail = ExperianCreditDetail;
type Bankruptcy = Dtos.CreditReport.Individual.Admin.Experian.Bankruptcy;
type Lien = Dtos.CreditReport.Individual.Admin.Experian.Lien;
type Judgment = Dtos.CreditReport.Individual.Admin.Experian.Judgment;
type Collection = Dtos.CreditReport.Individual.Admin.Experian.Collection;

type RecordCardProps = {
	label: string;
	count: number;
	caption: Option<string>;
};

const RecordCard: FC<RecordCardProps> = ({ label, count, caption }) => (
	<MetricCard
		data-testid={`individual_credit_record_card_${label.toLowerCase().replace(/\s+/g, "_")}`}
	>
		<div className="flex items-center justify-between gap-2">
			<MetricCard.Label size="md">{label}</MetricCard.Label>
			<Text type="eyebrow" color={count === 0 ? "confirm" : "danger"}>
				{count === 0 ? "Clear" : "On file"}
			</Text>
		</div>
		<MetricCard.Value size="lg">{count}</MetricCard.Value>
		<MetricCard.Caption>{count === 0 ? "No records" : caption}</MetricCard.Caption>
	</MetricCard>
);

type RecordFact = { label: string; value: Option<string> };

type RecordRowProps = {
	title: string;
	subtitle: Option<string>;
	disputed: boolean;
	facts: RecordFact[];
};

const RecordRow: FC<RecordRowProps> = ({ title, subtitle, disputed, facts }) => (
	<div
		className="flex flex-col gap-4 border-b border-bc-subtle px-5 py-4 last:border-b-0"
		data-testid="individual_credit_record_row"
	>
		<div className="flex flex-wrap items-center justify-between gap-3">
			<div className="flex items-baseline gap-2.5">
				<Text type="title" color="primary">
					{title}
				</Text>
				{Option.isSome(subtitle) && (
					<Text type="body" color="tertiary">
						{subtitle}
					</Text>
				)}
			</div>
			<Tag color={disputed ? "yellow" : "gray"}>
				{disputed ? "Disputed by consumer" : "Not disputed"}
			</Tag>
		</div>
		<div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
			{facts
				.filter((fact) => Option.isSome(fact.value))
				.map((fact) => (
					<Fact key={fact.label} label={fact.label}>
						<Text type="body" color="primary">
							{fact.value}
						</Text>
					</Fact>
				))}
		</div>
	</div>
);

const statusWithDate = (status: string | undefined, date: string | undefined) =>
	[status, Option.map(date, formatReportDate)].filter(Option.isSome).join(" ") || undefined;

const bankruptcyRow = (record: Bankruptcy): RecordRowProps => ({
	title: "Bankruptcy",
	subtitle:
		[
			Option.map(record.chapter, (chapter) => `Chapter ${chapter}`),
			Option.map(record.filing, (filing) => (filing === "voluntary" ? "Voluntary" : "Involuntary")),
		]
			.filter(Option.isSome)
			.join(" · ") || undefined,
	disputed: record.disputed,
	facts: [
		{ label: "Filed", value: Option.map(record.filedOn, formatReportDate) },
		{ label: "Status", value: statusWithDate(record.status, record.statusDate) },
		{ label: "Liabilities", value: formatReportDollars(record.liabilities) },
		{ label: "Assets", value: formatReportDollars(record.assets) },
		{ label: "Court", value: record.court },
		{ label: "Docket", value: record.docket },
	],
});

const lienRow = (record: Lien): RecordRowProps => ({
	title: "Lien",
	subtitle: formatReportDollars(record.amount),
	disputed: record.disputed,
	facts: [
		{ label: "Filed", value: Option.map(record.filedOn, formatReportDate) },
		{ label: "Status", value: statusWithDate(record.status, record.statusDate) },
		{ label: "Court", value: record.court },
		{ label: "Reference", value: record.docket },
	],
});

const judgmentRow = (record: Judgment): RecordRowProps => ({
	title: "Civil judgment",
	subtitle: formatReportDollars(record.amount),
	disputed: record.disputed,
	facts: [
		{ label: "Plaintiff", value: record.plaintiff },
		{ label: "Filed", value: Option.map(record.filedOn, formatReportDate) },
		{ label: "Status", value: statusWithDate(record.status, record.statusDate) },
		{ label: "Court", value: record.court },
		{ label: "Docket", value: record.docket },
	],
});

const collectionRow = (record: Collection): RecordRowProps => ({
	title: "Collection",
	subtitle: record.agency,
	disputed: record.disputed,
	facts: [
		{ label: "Original creditor", value: record.originalCreditor },
		{ label: "Balance", value: formatReportDollars(record.balance) },
		{ label: "Placed", value: Option.map(record.placedOn, formatMonthYear) },
		{ label: "Status", value: record.status },
	],
});

const bankruptcyCaption = (records: Bankruptcy[]): string | undefined => {
	const latest = records[0];
	if (Option.isNone(latest)) return undefined;
	return [
		Option.map(latest.chapter, (chapter) => `Ch. ${chapter}`),
		Option.map(latest.filedOn, (filed) => `filed ${DateTime.fromISO(filed).year}`),
	]
		.filter(Option.isSome)
		.join(" · ");
};

const balanceCaption = (amounts: (number | undefined)[]): string | undefined => {
	const known = amounts.filter(Option.isSome);
	return known.length > 0 ? `${formatReportDollars(Currency.sum(known))} balance` : undefined;
};

export const PublicRecordsSection: FC<{ detail: Detail }> = ({ detail }) => {
	const { bankruptcies, liens, judgments, collections } = detail.publicRecords;
	const derogatory = derogatoryCount(detail);
	const rows = [
		...bankruptcies.map(bankruptcyRow),
		...liens.map(lienRow),
		...judgments.map(judgmentRow),
		...collections.map(collectionRow),
	];

	return (
		<ReportSection
			title="Public Records & Derogatory"
			testId="individual_credit_public_records"
			summary={
				derogatory > 0 ? (
					<StatusSummary tone="danger">Public records on file</StatusSummary>
				) : (
					<StatusSummary tone="confirm">No public records</StatusSummary>
				)
			}
		>
			<div className="grid grid-cols-2 gap-3 md:grid-cols-4">
				<RecordCard
					label="Bankruptcies"
					count={bankruptcies.length}
					caption={bankruptcyCaption(bankruptcies)}
				/>
				<RecordCard
					label="Liens"
					count={liens.length}
					caption={balanceCaption(liens.map((lien) => lien.amount))}
				/>
				<RecordCard
					label="Judgments"
					count={judgments.length}
					caption={balanceCaption(judgments.map((judgment) => judgment.amount))}
				/>
				<RecordCard
					label="Collections"
					count={collections.length}
					caption={balanceCaption(collections.map((collection) => collection.balance))}
				/>
			</div>
			{rows.length > 0 && (
				<Card className="overflow-hidden rounded-xl">
					{rows.map((row, index) => (
						<RecordRow key={`${row.title}-${index}`} {...row} />
					))}
				</Card>
			)}
		</ReportSection>
	);
};
